import { Fragment, useEffect, useState } from 'react'
import { Box, Typography, IconButton, Icon, Avatar } from '@mui/material'
import { format, parseISO, addDays, subDays, differenceInYears } from 'date-fns'

import { useDailyDate } from 'services/hooks/useDate'
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import defaultAvatar from 'assets/images/default_avatar.png'

const CalendarDrawer = ({ state, handleDrawer, data, setData, calendarEvents, tabState }) => {
  const { day } = useDailyDate({ date: data })
  const [publicHolidayEvent, setPublicHolidayEvent] = useState([])
  const [leaveEvent, setLeaveEvent] = useState([])
  const [birthdayEvent, setBirthdayEvent] = useState([])
  const [anniversaryEvent, setAnniversaryEvent] = useState([])

  const groupBy = (items, key) =>
    items.reduce(
      (result, item) => ({
        ...result,
        [item[key]]: [...(result[item[key]] || []), item],
      }),
      {}
    )

  useEffect(() => {
    if (day) {
      const events =
        calendarEvents.length > 0 &&
        calendarEvents?.find((item) => format(parseISO(item[0]), 'dd MMMM yyyy') === day.fullDate)
      if (events) {
        setPublicHolidayEvent(events[1].holiday)
        setLeaveEvent(groupBy(events[1].leave_request, 'department'))
        setBirthdayEvent(groupBy(events[1].birthday, 'department'))
        setAnniversaryEvent(groupBy(events[1].work_anniversary, 'department'))
      } else {
        setPublicHolidayEvent([])
        setLeaveEvent({})
        setBirthdayEvent({})
        setAnniversaryEvent({})
      }
    }
  }, [day, calendarEvents])

  const HeaderProps = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: 1,
        }}
      >
        <Typography variant="h3" color="dark-text.main" sx={{ fontWeight: 'bold', width: '220px' }}>
          {format(day.object, `EE, dd/MM/yyyy`)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setData(subDays(data, 1))}
            sx={{
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'light-gray-text-2.main',
            }}
          >
            <Icon sx={{ color: 'light-gray-text.main' }}>chevron_left</Icon>
          </IconButton>
          <IconButton
            onClick={() => setData(addDays(data, 1))}
            sx={{
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'light-gray-text-2.main',
            }}
          >
            <Icon sx={{ color: 'light-gray-text.main' }}>chevron_right</Icon>
          </IconButton>
        </Box>
      </Box>
    )
  }
  return (
    <AdxDrawerComponent
      state={state}
      anchor="right"
      onClose={handleDrawer}
      headerprops={<HeaderProps />}
      noPaddingContent
    >
      <Box display="flex" flexDirection="column" gap={2} sx={{ pb: 3 }}>
        {publicHolidayEvent.length
          ? publicHolidayEvent.map((event) => (
              <Box
                key={event.name}
                sx={{
                  backgroundColor: 'gray-background.main',
                  width: 1,
                  height: '35px',
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  gap: 1,
                }}
              >
                <Box minWidth="2px" width="2px" height="28px" bgcolor="information.main" />
                <Typography noWrap variant="caption" color="information.main">
                  {event.name}
                </Typography>
              </Box>
            ))
          : null}

        {Object.keys(leaveEvent).length ||
        Object.keys(birthdayEvent).length ||
        Object.keys(anniversaryEvent).length ? (
          <AdxTabsBadge scrollButtons={false} sxPanel={{ py: 2, px: 2 }} initialValue={tabState}>
            {/* Leave request events tab */}
            {Object.keys(leaveEvent).length ? (
              <AdxTabsPanel label="On leave">
                {Object.entries(leaveEvent).map(([department, events], index) => (
                  <Fragment key={index}>
                    <Typography variant="subtitle2" color="light-gray-text.main">
                      {department} ({events.length})
                    </Typography>
                    <Box my={2} display="flex" flexDirection="column" gap={1.5}>
                      {events.map((event, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={2}>
                          <Avatar
                            alt="profile"
                            sx={{
                              width: 50,
                              height: 50,
                              bgcolor: 'alternate.main',
                            }}
                            src={event.staff_image || defaultAvatar}
                          />
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body1" color="dark-text.main">
                              {event.staff_name}
                            </Typography>
                            <Typography variant="body2" color="light-gray-text.main">
                              {event.leave_policy}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Fragment>
                ))}
              </AdxTabsPanel>
            ) : null}
            {/* Work anniversary events tab */}
            {Object.keys(anniversaryEvent).length ? (
              <AdxTabsPanel label="Work anniversary">
                {Object.entries(anniversaryEvent).map(([department, events], index) => {
                  return (
                    <Fragment key={index}>
                      <Typography variant="subtitle2" color="light-gray-text.main">
                        {department} ({events.length})
                      </Typography>
                      <Box my={2} display="flex" flexDirection="column" gap={1.5}>
                        {events.map((event, index) => (
                          <Box key={index} display="flex" alignItems="center" gap={2}>
                            <Avatar
                              alt="profile"
                              sx={{
                                width: 50,
                                height: 50,
                                bgcolor: 'alternate.main',
                              }}
                              src={event.staff_image || defaultAvatar}
                            />
                            <Box display="flex" flexDirection="column">
                              <Typography variant="body1" color="dark-text.main">
                                {event.staff_name}
                              </Typography>

                              <Typography variant="caption" color="light-gray-text.main">
                                {differenceInYears(day.object, parseISO(event.commencement_date))}{' '}
                                {differenceInYears(
                                  day.object,
                                  parseISO(event.commencement_date)
                                ) === 1
                                  ? 'year'
                                  : 'years'}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Fragment>
                  )
                })}
              </AdxTabsPanel>
            ) : null}
            {/* Birthday events tab */}
            {Object.keys(birthdayEvent).length ? (
              <AdxTabsPanel label="Birthday">
                {Object.entries(birthdayEvent).map(([department, events], index) => (
                  <Fragment key={index}>
                    <Typography variant="subtitle2" color="light-gray-text.main">
                      {department} ({events.length})
                    </Typography>
                    <Box my={2} display="flex" flexDirection="column" gap={1.5}>
                      {events.map((event, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={2}>
                          <Avatar
                            alt="profile"
                            sx={{
                              width: 50,
                              height: 50,
                              bgcolor: 'alternate.main',
                            }}
                            src={event.staff_image || defaultAvatar}
                          />
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body1" color="dark-text.main">
                              {event.staff_name}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Fragment>
                ))}
              </AdxTabsPanel>
            ) : null}
          </AdxTabsBadge>
        ) : null}
      </Box>
    </AdxDrawerComponent>
  )
}

export default CalendarDrawer
